import { React, useState} from 'react';
import {
    Box,
    Text,
    Flex,
    Button,
    Divider,
    SimpleGrid,
    VStack,
    Icon,
    useColorModeValue,
} from '@chakra-ui/react';

import SimpleSidebar from "views/global/SimpleSidebar";
import BoxDataTable from "views/global/BoxDataTable";
import SidebarScroll from 'components/scroll/SidebarScroll';
import { QuickAlert } from "theme/components/alerts";
import { Euro, Pencil, Eye, Laptop, Calendar, Building, BedDouble, Send, Link2, PlusCircle, MinusCircle, StickyNote, CreditCard, Banknote, Wallet, Landmark } from "lucide-react";
import handleSidebar from "lib/handleSidebar";
import { userController } from 'lib/user';
import FormGenerate from "views/global/FormGenerate";
import BeatLoader from "react-spinners/BeatLoader";
import { cityTaxStatusLabels, paymentMethodLabels, notificationStatusLabels, notificationTypeLabel, toOptions, labelFor } from 'lib/labels';

function InfoRow({ label, value, labelColor }) {
    if (!value) return null;
    return (
        <Flex align="baseline" gap="8px" py="5px" borderBottom="1px solid" borderColor="gray.100">
            <Text fontSize="12px" fontWeight="600" color={labelColor} minW="140px" textTransform="uppercase" letterSpacing="0.05em">
                {label}
            </Text>
            <Text fontSize="14px" color="gray.800">{value}</Text>
        </Flex>
    );
}


// Salvato in minuscolo ("stripe"/"cash"/"pos"/"bank_transfer"/"sumup"); mostrato tradotto.
function paymentMethodLabel(method) {
    if (!method) return null;
    // "Altro" non è più selezionabile, ma resta sulle righe salvate in precedenza.
    if (method === 'other') return "Altro";
    return labelFor(paymentMethodLabels, method);
}

const paymentMethodIcons = {
    stripe: CreditCard,
    cash: Banknote,
    pos: CreditCard,
    bank_transfer: Landmark,
    sumup: CreditCard,
};

// Niente metodo salvato (non pagata, o pagata prima del backfill) → niente icona, niente testo.
function PaymentMethodTag({ method, ...rest }) {
    if (!method) return null;
    const MethodIcon = paymentMethodIcons[method] ?? Wallet;
    return (
        <Text className="muted-text d-flex d-icon-5 d-icon-fx-18" {...rest}>
            <MethodIcon/> {paymentMethodLabel(method)}
        </Text>
    );
}

function CityTaxTab(props) {

    const { id, title } = props;

    const { setAlert, clearAlert, showAlert } = QuickAlert();
    const { saveRecord, getSingleRecord } = userController();
    const [input, setInput] = useState(id ? {property_id: id} : null);
    const [savedRecord, setSavedRecord] = useState(null);
    const [notifLoading, setNotifLoading] = useState(false);
    const [linkLoading, setLinkLoading] = useState(false);
    const defaultOptions = id ? { property_id: id } : {};

    const [status, setStatus] = useState({
        sidebarForm: false,
        loading: false,
        formLoading: false,
        saveNewList: false,
        editListEnabled: false,
        viewListEnabled: false,
    });
    const trigerSaveList = status.saveNewList ? false : true;

    const [viewDetail, setViewDetail] = useState(null);
    const [notifications, setNotifications] = useState([]);
    const [notifExpanded, setNotifExpanded] = useState(false);

    const mutedColor  = useColorModeValue('gray.500', 'whiteAlpha.600');
    const labelColor  = useColorModeValue('gray.600', 'whiteAlpha.600');
    const rowBg       = useColorModeValue('gray.50',  'whiteAlpha.50');

    // Confirmed via a real Stripe payment — status and payment method can't be moved
    // off "paid"/"stripe", but amount/due date/note stay freely editable, same as any
    // other record.
    const stripePaid = savedRecord?.stripe_session_id && savedRecord?.status === 'paid';

    const fieldNames = [
        {label: "Importo", name:"amount", type:"text", required: false},
        {label: "Valuta", name:"currency", type:"select", required: false, options: ['EUR']},
        {label: "Scadenza", name:"due_date", type:"date", required: true},
        ...(stripePaid
            // Locked pair: status stays "paid", method stays "stripe" (shown, not editable).
            ? [{label: "Metodo di Pagamento", name:"payment_method", type:"select", options: [["Stripe","stripe"]], disabled: true}]
            // Otherwise the method is asked for — and only makes sense — once "paid" is picked.
            : [
                {label: "Stato", name:"status", type:"select", options: toOptions(cityTaxStatusLabels)},
                ...(input?.status === 'paid'
                    ? [{label: "Metodo di Pagamento", name:"payment_method", type:"select", required: true, options: toOptions(paymentMethodLabels)}]
                    : []
                ),
            ]
        ),
        {label: "Nota", name:"note", type:"text", required: false},
    ];

    const sendPaymentNotification = (reservationId) => {
        setNotifLoading(true);
        clearAlert();
        saveRecord('/user/reservation/payment-notification', { reservation_id: reservationId })
            .then(response => {
                setNotifLoading(false);
                setAlert("success", response.data.message);
            })
            .catch(error => {
                setNotifLoading(false);
                setAlert("error", error.response.data.errors);
                SidebarScroll();
            });
    };

    const generatePaymentLink = (reservationId) => {
        setLinkLoading(true);
        clearAlert();
        saveRecord('/user/reservation/payment-link/generate', { reservation_id: reservationId })
            .then(response => {
                setLinkLoading(false);
                setAlert("success", response.data.message);
                setViewDetail(prev => prev ? { ...prev, link: response.data.link } : prev);
                setStatus(prev => ({ ...prev, saveNewList: trigerSaveList }));
            })
            .catch(error => {
                setLinkLoading(false);
                setAlert("error", error.response.data.errors);
                SidebarScroll();
            });
    };

    const openEdit = (row) => {
        setStatus(prevStatus => ({
            ...prevStatus,
            sidebarForm: true,
            editListEnabled: true,
            viewListEnabled: false,
            formLoading: false,
        }));
        setInput((prev) => ({ ...prev, ...row }));
        setSavedRecord(row);
        handleSidebar();
    };

    const openView = (row) => {
        setViewDetail(row);
        setNotifications([]);
        setNotifExpanded(false);
        clearAlert();
        setStatus(prevStatus => ({
            ...prevStatus,
            sidebarForm: true,
            editListEnabled: false,
            viewListEnabled: true,
        }));
        handleSidebar();
        getSingleRecord('/user/reservation/notification/list?reservation_id=' + row.reservation_id)
            .then(response => setNotifications(response.data.rows || []))
            .catch(() => setNotifications([]));
    };

    const tableMenuItems = [
        {
            icon: <Eye />,
            title: "Visualizza Tassa di Soggiorno",
            handle: (row) => openView(row),
        },
        {
            icon: <Pencil />,
            title: "Modifica Tassa di Soggiorno",
            handle: (row) => openEdit(row),
        },
    ];

    const hideList = () => {
        setStatus(prevStatus => ({
            ...prevStatus,
            sidebarForm: false,
            editListEnabled: false,
            viewListEnabled: false,
        }));
        setInput(id ? {property_id: id} : null);
        setViewDetail(null);
        handleSidebar(false);
        clearAlert();
    }

    const saveListHandle = () => {
        setStatus(prevStatus => ({
            ...prevStatus,
            loading: true,
        }));
        saveRecord('/user/city-tax/update',input).then(response => {
            return response;
        }).then(response => {
            setStatus(prevStatus => ({
                ...prevStatus,
                loading: false,
                saveNewList: trigerSaveList,
            }));
            hideList();
        }).catch(function(error) {
            setStatus(prevStatus => ({
                ...prevStatus,
                loading: false,
            }));
            setAlert("error",error.response.data.errors);
            SidebarScroll();
        });
    }

    return (
        <>
        <Box>
            {status.sidebarForm===false ? showAlert() : null }
            <BoxDataTable
                title={title}
                src="/user/city-tax/list"
                additionalOptions={defaultOptions}
                defaultFilters={{ period: '72' }}
                search={true}
                checkbox={false}
                tableMenu={tableMenuItems}
                trigger={status.saveNewList}
                design="dragable"
                dragableType="CityTax"
                defaultCalander="reservation_check_in"
                tableContent={(item)=>{
                    const dotColor = item.status === 'paid' ? 'green.400' : item.status === 'exempted' ? 'blue.400' : 'orange.400';
                    return (
                        <Box
                        key={item.id}
                        cursor="pointer"
                        onClick={() => openView(item)}
                        >
                            <Text className={item.status+" reservation-item-title"}>
                                <Box as="span" display="inline-block" w="9px" h="9px" borderRadius="full" me="6px" position="relative" top="0px" bg={dotColor} />
                                {item.reservation.name}
                            </Text>
                            {item.reservation.email &&
                                <Text mb={2}><small>{item.reservation.email}</small></Text>
                            }
                            <Flex
                            display={{base:"block", md:"flex"}}
                            >
                                {item.amount && <Text className="muted-text d-flex d-icon-5 d-icon-fx-18" me={4}><Euro/> {item.amount}</Text>}
                                <PaymentMethodTag method={item.payment_method} me={4} />
                                {item.note &&<Text className="muted-text d-flex d-icon-5" me={4}><StickyNote/> {item.note}</Text>}
                                {item.reservation?.check_in && <Text className="muted-text d-flex d-icon-5" me={4}> <Calendar/> {item.reservation.check_in}</Text>}
                                {item.link && <Text className="muted-text d-flex d-icon-5" me={4}><Laptop/> <a target='_blank' className='primary-link' href={item.link}>{(() => {
                            const cleanedLink = item.link.replace(/^https?:\/\/(www\.)?/, '');
                            return cleanedLink.length > 40 ? `${cleanedLink.substring(0, 40)}...` : cleanedLink;
                        })()}</a></Text>}
                                <Text className="muted-text d-flex d-icon-5 d-icon-fx-18"><Building/> {item.reservation.property.identifier}</Text>
                                {item.reservation?.property_room && (
                                    <Text className="muted-text d-flex d-icon-5 d-icon-fx-18" ms={4}>
                                        <BedDouble/> {item.reservation.property_room.description}
                                    </Text>
                                )}
                            </Flex>
                        </Box>
                    )
                }}
            />
            {/* ── Edit sidebar ── */}
            <SimpleSidebar
            status={status.sidebarForm && status.editListEnabled}
            title="Modifica Tassa di Soggiorno"
            save={{onClick:saveListHandle,isLoading:status.loading,text:"Aggiorna Tassa di Soggiorno"}}
            cancel={{onClick:hideList, text:"Annulla"}}
            loading={status.formLoading}
            >
                {status.editListEnabled && showAlert()}
                {status.editListEnabled && stripePaid && (
                    <Text fontSize="13px" color={mutedColor} mb="16px">
                        Stato e metodo di pagamento sono bloccati perché il pagamento è stato confermato tramite Stripe — tutto il resto resta modificabile.
                    </Text>
                )}
                {status.editListEnabled &&
                    <FormGenerate fields={fieldNames} setFields={setInput} defaultValue={input} />
                }
            </SimpleSidebar>

            {/* ── View sidebar ── */}
            <SimpleSidebar
                status={status.sidebarForm && status.viewListEnabled}
                title="Dettagli Tassa di Soggiorno"
                cancel={{ onClick: hideList, text: 'Chiudi' }}
                viewOnly={true}
                leftAction={viewDetail?.status === 'pending' ? (
                    <Flex gap="8px">
                        {!viewDetail?.link && (
                            <Button
                                variant="outline"
                                colorScheme="brand"
                                fontWeight="light"
                                leftIcon={linkLoading ? null : <Link2 />}
                                _hover={{ bg: 'brand.500', color: 'white' }}
                                transition="background-color 0.2s linear"
                                isDisabled={linkLoading}
                                onClick={() => generatePaymentLink(viewDetail.reservation_id)}
                            >
                                {linkLoading
                                    ? <BeatLoader loading={true} size={8} aria-label="Loading Spinner" color="#422CFA" />
                                    : 'Genera Link'
                                }
                            </Button>
                        )}
                        <Button
                            variant="outline"
                            colorScheme="brand"
                            fontWeight="light"
                            leftIcon={notifLoading ? null : <Send />}
                            _hover={{ bg: 'brand.500', color: 'white' }}
                            transition="background-color 0.2s linear"
                            isDisabled={notifLoading}
                            onClick={() => sendPaymentNotification(viewDetail.reservation_id)}
                        >
                            {notifLoading
                                ? <BeatLoader loading={true} size={8} aria-label="Loading Spinner" color="#422CFA" />
                                : 'Invia Promemoria'
                            }
                        </Button>
                    </Flex>
                ) : null}
            >
                {status.viewListEnabled && showAlert()}

                {status.viewListEnabled && viewDetail && (
                    <Box>
                        {/* Booking summary */}
                        <Box bg={rowBg} borderRadius="12px" p="16px" mb="20px">
                            <Text fontSize="11px" fontWeight="700" color={labelColor} textTransform="uppercase" letterSpacing="0.08em" mb="10px">
                                Riepilogo Prenotazione
                            </Text>
                            {viewDetail.reservation?.property?.identifier && (
                                <Text fontSize="18px" fontWeight="700" color="gray.800" mb="2px">
                                    {viewDetail.reservation.property.identifier}
                                </Text>
                            )}
                            <Text fontSize="16px" fontWeight="700" color="gray.800" mb="2px">
                                {viewDetail.reservation?.name}
                            </Text>
                            <Text fontSize="13px" color={mutedColor} mb="12px">
                                Prenotazione #{viewDetail.reservation_id ?? viewDetail.reservation?.id}
                            </Text>
                            <SimpleGrid columns={2} gap="8px" mb="8px">
                                <Box bg="white" borderRadius="8px" px="12px" py="8px">
                                    <Text fontSize="11px" color={mutedColor} fontWeight="600" textTransform="uppercase" letterSpacing="0.05em">Check-in</Text>
                                    <Text fontSize="14px" fontWeight="600">{viewDetail.reservation?.check_in ?? '—'}</Text>
                                </Box>
                                <Box bg="white" borderRadius="8px" px="12px" py="8px">
                                    <Text fontSize="11px" color={mutedColor} fontWeight="600" textTransform="uppercase" letterSpacing="0.05em">Check-out</Text>
                                    <Text fontSize="14px" fontWeight="600">{viewDetail.reservation?.check_out ?? '—'}</Text>
                                </Box>
                            </SimpleGrid>
                            {viewDetail.link ? (
                                <Flex align="center" gap="6px" bg="white" borderRadius="8px" px="12px" py="8px">
                                    <Icon as={Link2} color="brand.500" boxSize="14px" flexShrink={0} />
                                    <Text fontSize="11px" color={mutedColor} fontWeight="600" textTransform="uppercase" letterSpacing="0.05em" flexShrink={0}>Link di Pagamento</Text>
                                    <Text
                                        as="a"
                                        href={viewDetail.link}
                                        target="_blank"
                                        fontSize="13px"
                                        color="brand.500"
                                        fontWeight="500"
                                        noOfLines={1}
                                        overflow="hidden"
                                        textOverflow="ellipsis"
                                        _hover={{ textDecoration: 'underline' }}
                                    >
                                        {viewDetail.link}
                                    </Text>
                                </Flex>
                            ) : (
                                <Flex align="center" gap="6px" bg="white" borderRadius="8px" px="12px" py="8px">
                                    <Icon as={Link2} color="gray.300" boxSize="14px" flexShrink={0} />
                                    <Text fontSize="11px" color={mutedColor} fontWeight="600" textTransform="uppercase" letterSpacing="0.05em" flexShrink={0}>Link di Pagamento</Text>
                                    <Text fontSize="13px" color="gray.400">Nessun link generato</Text>
                                </Flex>
                            )}
                        </Box>

                        {/* Status */}
                        <Flex align="center" gap="8px" mb="20px">
                            <Box
                                w="10px" h="10px" borderRadius="full" flexShrink={0}
                                bg={viewDetail.status === 'paid' ? 'green.400' : viewDetail.status === 'exempted' ? 'blue.400' : 'orange.400'}
                            />
                            <Text fontSize="14px" fontWeight="600" color={viewDetail.status === 'paid' ? 'green.600' : viewDetail.status === 'exempted' ? 'blue.500' : 'orange.500'} >
                                {labelFor(cityTaxStatusLabels, viewDetail.status)}
                            </Text>
                        </Flex>

                        <Divider mb="20px" />

                        <VStack spacing={0} align="stretch" mb="20px">
                            <InfoRow label="Importo"   value={viewDetail.amount ? `${viewDetail.amount} ${viewDetail.currency ?? ''}`.trim() : null} labelColor={labelColor} />
                            <InfoRow label="Pagato con" value={paymentMethodLabel(viewDetail.payment_method)} labelColor={labelColor} />
                            <InfoRow label="Scadenza"  value={viewDetail.due_date}  labelColor={labelColor} />
                            <InfoRow label="Nota"      value={viewDetail.note}       labelColor={labelColor} />
                            {viewDetail.reservation?.email && (
                                <InfoRow label="Email Ospite" value={viewDetail.reservation.email} labelColor={labelColor} />
                            )}
                        </VStack>

                        <Divider mb="20px" />

                        {/* Notification history */}
                        <Box className='reservation-tasks-list' marginBottom={4}>
                            <Flex className="rtl-title" justify="space-between" align="center" cursor="pointer" onClick={() => setNotifExpanded(p => !p)}>
                                <span>Storico Notifiche</span>
                                {notifExpanded ? <MinusCircle /> : <PlusCircle />}
                            </Flex>
                            {notifExpanded && (
                                notifications.length === 0 ? (
                                    <Text className="muted-text">Nessuna notifica inviata.</Text>
                                ) : (
                                    <ul>
                                        {notifications.map((n, i) => (
                                            <li key={"notif_" + i}>
                                                <Text className='rtl-task-title'>{notificationTypeLabel(n.type)}</Text>
                                                <Flex className='task-meta'>
                                                    <Text className="muted-text d-flex d-icon-5 d-icon-fx-18" me={4}><Send /> {n.recipient}</Text>
                                                    <Text className={"muted-text d-flex d-icon-5 d-icon-fx-18 " + n.status} textTransform="capitalize" me={4}>{labelFor(notificationStatusLabels, n.status)}</Text>
                                                    <Text className="muted-text d-flex d-icon-5" me={4}><Calendar /> {n.sent_at}</Text>
                                                </Flex>
                                            </li>
                                        ))}
                                    </ul>
                                )
                            )}
                        </Box>
                    </Box>
                )}
            </SimpleSidebar>
        </Box>
        </>
    );
}
export default CityTaxTab;
